import React from "react";
import { SimpleTableDesktopView } from "./SimpleTableDesktopView";
import { SimpleTableContextProvider } from "./contexts/SimpleTableContext";
import { useSimpleTableCallbacks } from "./hooks/useSimpleTableCallbacks";

// Import setup modules
import { useSimpleTableSetup } from "./SimpleTableSetup";
import { extractSimpleTableState } from "./SimpleTableStateSetup";
import { createSimpleTableCallbackWrappers } from "./SimpleTableCallbacksSetup";
import { createSimpleTableContextValue } from "./SimpleTableContextValue";
import { renderSimpleTableConditional } from "./SimpleTableConditionalRenders";

// ============================================================================
// TYPES - Imported from shared types
// ============================================================================

import type { BaseEntity, SimpleTableProps } from "./types";
import type { SimpleTableSetupResult } from "./SimpleTableSetup";

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function SimpleTable<TData extends BaseEntity>({
  config,
  data = [],
  isLoading = false,
  error = null,
  onEdit,
  onView,
  onInlineEdit,
  onDelete,
  onCreate,
  onBulkAction,
  savedViewsControls,
  mobileHeaderActions,
  onSavedViewsReady,
  loadMore,
  hasNextPage = false,
  isFetchingNextPage = false,
  autoScrollEnabled,
  // Flag to indicate if data is already filtered
  isDataPreFiltered = false,
  // Saved Views (optional)
  savedViews,
  onApplySavedView,
  onSaveView,
  onDeleteSavedView,
  // Export (optional)
  onExport,
  // Pagination (optional)
  currentPage: propCurrentPage,
  totalPages: propTotalPages,
  pageSize: propPageSize,
  totalItems: propTotalItems,
  onPageChange: propOnPageChange,
  onPageSizeChange: propOnPageSizeChange,
  // Entity hooks for actions
  hooks,
  groupingContext,
  groupableFields,
  // Detail modal navigation
  onOpenDetailModal,
  // External filters (mobile list + server queries)
  externalFilters,
  onFiltersChange,
  filters: propTableFilters,
  setFilters: propSetFilters,
  // Hide table body (useful for header-only view in card mode)
  hideTableBody = false,
  // Hide column headers (useful for card view)
  hideColumnHeaders = false,
}: SimpleTableProps<TData>) {
  // ============================================================================
  // SETUP PHASE 1: Initialize all hooks and state
  // ============================================================================

  // The table renders the FULL row set (never a page window) when the caller is
  // in infinite-scroll mode (rows accumulate as the user scrolls) or when
  // grouping is active (rows include group-header entries). In every other case
  // pagination windowing applies so a data source that returns the whole result
  // set — RPC filters, FTS search — still shows one page at a time.
  const disableClientSlice =
    loadMore != null || autoScrollEnabled === true || groupingContext?.isGrouped === true;

  const setup = useSimpleTableSetup<TData>({
    config,
    data,
    propCurrentPage,
    propOnPageChange,
    propPageSize,
    propOnPageSizeChange,
    isDataPreFiltered,
    propTotalItems,
    disableClientSlice,
  });

  // ============================================================================
  // SYNC QUICK FILTERS TO FILTERCONTEXT
  // ============================================================================

  // When quick filters change, sync them to FilterContext
  // This ensures quick filters are sent to the server via GraphQL
  React.useEffect(() => {
    if (!setup.quickFilterState || Object.keys(setup.quickFilterState).length === 0) return;
    if (!setup.filterContext) return; // Safety check

    // Convert quick filter state to simple filters format
    Object.entries(setup.quickFilterState).forEach(([filterId, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        setup.filterContext?.actions.setSimpleFilter(filterId, value);
      }
    });
  }, [setup.quickFilterState, setup.filterContext]);

  // ============================================================================
  // SETUP PHASE 2: Get callbacks from hook
  // ============================================================================

  const callbacks = useSimpleTableCallbacks({
    config,
    data,
    state: setup.state,
    hooks,
    appUser: setup.appUser,
    onInlineEdit: onInlineEdit
      ? (payload: Record<string, any>) => onInlineEdit(payload as TData)
      : undefined,
    onBulkAction,
    onSavedViewsReady,
    contextVisibleColumns: setup.contextVisibleColumns.map((col: any) => col.id),
    contextToggleColumn: setup.contextToggleColumn,
    quickFilterState: setup.quickFilterState,
    setQuickFilterState: setup.setQuickFilterState,
  });

  // ============================================================================
  // SETUP PHASE 3: Extract and organize state
  // ============================================================================

  const stateSetup = extractSimpleTableState<TData>(setup);

  // ============================================================================
  // SETUP PHASE 4: Create callback wrappers
  // ============================================================================

  const callbackWrappers = createSimpleTableCallbackWrappers(stateSetup);

  // ============================================================================
  // SETUP PHASE 4.5: Create memoized handlers (MUST be before conditional return)
  // ============================================================================

  // Create handlers that include paginatedData
  const handleSelectAll = React.useMemo(() => {
    const handler = callbacks.createHandleSelectAll();
    return (selected: boolean) => handler(selected, setup.paginatedData);
  }, [callbacks.createHandleSelectAll, setup.paginatedData]);

  const handleBulkAction = React.useMemo(() => {
    const handler = callbacks.createHandleBulkAction();
    return (actionId: string) => handler(actionId, setup.paginatedData);
  }, [callbacks.createHandleBulkAction, setup.paginatedData]);

  // Wrapper for scroll handlers
  const handleBodyScroll = React.useCallback(() => {
    callbacks.handleBodyScroll(setup.tableBodyRef as React.RefObject<HTMLDivElement>, setup.tableHeaderScrollRef as React.RefObject<HTMLDivElement>);
  }, [callbacks, setup.tableBodyRef, setup.tableHeaderScrollRef]);

  const handleHeaderScroll = React.useCallback(() => {
    callbacks.handleHeaderScroll(setup.tableBodyRef as React.RefObject<HTMLDivElement>, setup.tableHeaderScrollRef as React.RefObject<HTMLDivElement>);
  }, [callbacks, setup.tableBodyRef, setup.tableHeaderScrollRef]);

  // ============================================================================
  // SETUP PHASE 5: Handle conditional rendering (loading, error, mobile)
  // ============================================================================

  const visibleColumnIdsForMobile = setup.visibleFields.map(([id]) => id);

  const conditionalRender = renderSimpleTableConditional<TData>(
    setup,
    {
      config,
      data,
      isLoading,
      error,
      onEdit,
      onView,
      onInlineEdit,
      onDelete,
      onCreate,
      onBulkAction,
      savedViewsControls,
      mobileHeaderActions,
      onSavedViewsReady,
      loadMore,
      hasNextPage,
      isFetchingNextPage,
      autoScrollEnabled,
      isDataPreFiltered,
      savedViews,
      onApplySavedView,
      onSaveView,
      onDeleteSavedView,
      onExport,
      currentPage: propCurrentPage,
      totalPages: propTotalPages,
      pageSize: propPageSize,
      totalItems: propTotalItems,
      onPageChange: propOnPageChange,
      onPageSizeChange: propOnPageSizeChange,
      hooks,
      groupingContext,
      groupableFields,
      onOpenDetailModal,
      externalFilters,
      onFiltersChange,
      filters: propTableFilters,
      setFilters: propSetFilters,
      visibleColumnIds: visibleColumnIdsForMobile,
    },
    isLoading,
    error
  );

  if (conditionalRender) {
    return conditionalRender;
  }

  // ============================================================================
  // SETUP PHASE 6: Create context value
  // ============================================================================

  const contextValue = createSimpleTableContextValue<TData>(
    setup,
    stateSetup,
    callbackWrappers,
    {
      ...callbacks,
      handleSelectAll,
      handleBulkAction,
      handleBodyScroll,
      handleHeaderScroll,
    },
    {
      config,
      data,
      isLoading,
      error,
      onEdit,
      onView,
      onInlineEdit,
      onDelete,
      onCreate,
      onBulkAction,
      savedViewsControls,
      mobileHeaderActions,
      onSavedViewsReady,
      loadMore,
      hasNextPage,
      isFetchingNextPage,
      autoScrollEnabled,
      isDataPreFiltered,
      savedViews,
      onApplySavedView,
      onSaveView,
      onDeleteSavedView,
      onExport,
      currentPage: propCurrentPage,
      totalPages: propTotalPages,
      pageSize: propPageSize,
      totalItems: propTotalItems,
      onPageChange: propOnPageChange,
      onPageSizeChange: propOnPageSizeChange,
      hooks,
      groupingContext,
      groupableFields,
      onOpenDetailModal,
      hideColumnHeaders,
      hideTableBody,
    }
  );

  // ============================================================================
  // RENDER DESKTOP VIEW
  // ============================================================================

  return (
    <SimpleTableContextProvider<TData> value={contextValue}>
      <SimpleTableDesktopView />
    </SimpleTableContextProvider>
  );
}
